import { Remove, Send } from "@mui/icons-material";
import { Alert, Badge, IconButton, Paper, Snackbar, TextField, Typography } from "@mui/material";
import { Box, Stack, styled } from "@mui/system";
import React, { useEffect, useState } from "react";
import { BotReplyNewApi, BotReplyOldApi } from "../../API/Api";
import "../ChatBot/FloatingContactMe.css";
import ChatBox from "./ChatBox";
import ghorami from "../../../images/songjukti_logo.png";
import useFormattedImageUrl from "../../../Utils/useFormattedImageUrl";
import { useStore } from "../../../store/useStore";
import { tokens } from "../../../store/tokens";
import LoginSignUpFormMini from "../../OrderCheckOut/LoginSignUpFormMini";
import { useParams } from "react-router-dom";

const decodeLocalData = (name) => {
  try {
    const encoded = window.localStorage.getItem(name);
    if (!encoded) return null;
    return JSON.parse(atob(encoded));
  } catch (error) {
    console.error("Failed to decode localStorage data:", error);
    return null;
  }
};

const FloatingContactMe = ({
  commesFrom,
  profileDetails,
  pic,
  logo,
  name,
  urlId,
  chatOpen,
  setChatOpen,
  botOwner,
}) => {
  const formattedLogo = useFormattedImageUrl(logo);
  const formattedPic = useFormattedImageUrl(pic);

  // useParams() always returns an object (never falsy), so
  // `useParams() || fallback` never fell back to anything — and even if
  // it had, you can't destructure a named field off a plain string.
  // Reads the actual param, falling back to the last URL segment only
  // when that param is genuinely missing.
  const params = useParams();
  const botOwnerGet = params?.botOwnerGet ?? window.location.pathname.split("/").pop();

  const { user, setUser } = useStore();
  const [chatText, setChatText] = useState("");
  const [allConversations, setAllConversations] = useState([]);
  const [loading, setLoading] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isLogin, setIsLogin] = useState(true);
  const [isLogged, setIsLogged] = useState(false);
  const [emptyText, setEmptyText] = useState(false);
  const [snackMessage, setSnackMessage] = useState("");
  const [snackOpen, setSnackOpen] = useState(false);

  const handleSnackOpen = (message) => {
    setSnackOpen(true);
    setSnackMessage(message);
  };

  const handleSnackClose = (_, reason) => {
    if (reason === "clickaway") return;
    setSnackOpen(false);
  };

  useEffect(() => {
    const isValidLogin = !!user?.sopnoid;
    setIsLogged(isValidLogin);

    const storedUser = decodeLocalData("gh_sl@g");
    if (!isValidLogin && storedUser) {
      try {
        const originMatches = storedUser?.origin === window.location.origin;
        const hasValidId = !!storedUser?.sopnoid;
        if (originMatches && hasValidId) {
          setUser(storedUser);
          setIsLogged(true);
        } else {
          localStorage.removeItem("gh_sl@g");
        }
      } catch (err) {
        console.error("Failed to parse stored user", err);
        localStorage.removeItem("gh_sl@g");
      }
    }
  }, [user]);

  const [hasFetchedOldMessages, setHasFetchedOldMessages] = useState(false);

  useEffect(() => {
    if (user?.sopnoid && botOwner && !hasFetchedOldMessages) {
      const formData = new FormData();
      formData.append("user_id", user?.sopnoid);
      formData.append("botOwner", botOwner ?? botOwnerGet);
      formData.append("commesFrom", commesFrom);

      fetch(BotReplyOldApi, { method: "POST", body: formData })
        .then((res) => res.json())
        .then((oldMessages) => {
          if (Array.isArray(oldMessages) && oldMessages[0]?.botOwner === botOwner) {
            setAllConversations((prev) => {
              const existingIds = new Set(prev.map((msg) => msg.id));
              const unique = oldMessages.filter((msg) => !existingIds.has(msg.id));
              return [...prev, ...unique];
            });
            setHasFetchedOldMessages(true);
          }
        })
        .catch(console.error);
    }
  }, [user?.sopnoid, botOwner, commesFrom, hasFetchedOldMessages]);

  const handleSendMessage = () => {
    if (!chatText.trim()) {
      setEmptyText(true);
      return;
    }

    const formData = new FormData();
    formData.append("user_id", user?.sopnoid);
    formData.append("mtext", chatText);
    formData.append("botOwner", botOwner ?? botOwnerGet);
    formData.append("commesFrom", commesFrom);

    fetch(BotReplyNewApi, { method: "POST", body: formData })
      .then((res) => res.json())
      .then((res) => {
        const newMessage = res?.[0];
        if (newMessage && newMessage.botOwner === botOwner) {
          setAllConversations((prev) => [...prev, newMessage]);
        }
        setChatText("");
        setEmptyText(false);
      })
      .catch(console.error);
  };

  const profileImage = commesFrom === "personal" ? formattedPic || formattedLogo || ghorami : formattedLogo || formattedPic || ghorami;

  const handleKeyDown = (event) => {
    if (event.key === "Enter" && !event.shiftKey) {
      event.preventDefault();
      handleSendMessage();
    }
  };

  return (
    <Box>
      <Snackbar open={snackOpen} autoHideDuration={4000} onClose={handleSnackClose} anchorOrigin={{ vertical: "bottom", horizontal: "center" }}>
        <Alert onClose={handleSnackClose} severity="info" sx={{ width: "100%" }}>
          {snackMessage}
        </Alert>
      </Snackbar>

      <Paper
        elevation={10}
        onClick={() => setChatOpen(!chatOpen)}
        className={chatOpen ? "smallButton" : ""}
        sx={{
          width: "230px",
          height: "60px",
          borderRadius: "50px",
          position: "fixed",
          right: "30px",
          bottom: "30px",
          zIndex: 20,
          cursor: "pointer",
          transition: "width 1s",
          "&:hover": { bgcolor: tokens.color.surfaceMuted },
        }}
      >
        <Stack spacing={2} direction={"row"} alignItems={"center"} sx={{ height: "60px" }}>
          <StyledBadge badgeContent={""} variant="dot">
            <Box
              component="img"
              sx={{ height: "50px", width: "50px", objectFit: "cover", cursor: "pointer", borderRadius: "50%", marginLeft: "6px" }}
              src={commesFrom === "personal" ? formattedPic ?? formattedLogo ?? ghorami : formattedLogo ?? formattedPic ?? ghorami}
            />
          </StyledBadge>
          <Stack sx={{ height: "50px", overflow: "hidden" }} className={chatOpen ? "hideWords" : ""}>
            <Typography variant="subtitle2" sx={{ color: tokens.color.ink }}>
              {name}
            </Typography>
            <Typography variant="caption" sx={{ color: tokens.color.inkMuted }}>
              Contact me
            </Typography>
          </Stack>
        </Stack>
      </Paper>

      {chatOpen && (
        <Paper
          elevation={10}
          sx={{
            height: user?.sopnoid ? "65vh" : "55vh",
            width: { xs: "90vw", md: "25vw" },
            position: "fixed",
            right: "15px",
            bottom: "91px",
            borderRadius: `${tokens.radius.lg}px`,
            zIndex: 30,
            display: "flex",
            flexDirection: "column",
            overflow: "hidden",
          }}
        >
          <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ bgcolor: tokens.color.tealDark, p: 1.5 }}>
            <Stack direction="row" alignItems="center" spacing={1}>
              <Box component="img" src={profileImage} sx={{ width: 40, height: 40, borderRadius: "50%" }} />
              <Box>
                <Typography variant="body1" sx={{ color: tokens.color.white, fontWeight: 600 }}>
                  {name}
                </Typography>
                <Typography variant="caption" sx={{ color: "rgba(255,255,255,0.75)" }}>
                  Powered by Songjukti
                </Typography>
              </Box>
            </Stack>
            <IconButton onClick={() => setChatOpen(false)}>
              <Remove sx={{ color: tokens.color.white }} />
            </IconButton>
          </Stack>

          <Box sx={{ flex: 1, overflowY: "hidden", p: 1 }}>
            {!user?.sopnoid ? (
              <LoginSignUpFormMini
                isLogin={isLogin}
                setIsLogin={setIsLogin}
                loading={loading}
                setLoading={setLoading}
                setIsSubmitting={setIsSubmitting}
                setIsLogged={setIsLogged}
                handleSnackOpen={handleSnackOpen}
                handleSnackClose={handleSnackClose}
              />
            ) : (
              <ChatBox
                profileDetails={profileDetails}
                pic={formattedPic}
                logo={formattedLogo}
                commesFrom={commesFrom}
                name={user?.name}
                urlId={urlId}
                botOldConversation={allConversations}
              />
            )}
          </Box>

          {user?.sopnoid && (
            <Stack direction="row" spacing={1} alignItems="center" p={1}>
              <TextField
                fullWidth
                value={chatText}
                onChange={(e) => setChatText(e.target.value)}
                onKeyDown={handleKeyDown}
                placeholder={emptyText ? "Type a message before sending" : "Type a message…"}
                variant="outlined"
                size="small"
                sx={{
                  bgcolor: tokens.color.surface,
                  "& .MuiOutlinedInput-root": { borderRadius: `${tokens.radius.pill}px` },
                }}
              />
              <IconButton onClick={handleSendMessage} sx={{ color: tokens.color.teal }}>
                <Send />
              </IconButton>
            </Stack>
          )}
        </Paper>
      )}
    </Box>
  );
};

export default FloatingContactMe;

const StyledBadge = styled(Badge)(() => ({
  "& .MuiBadge-badge": {
    right: 9,
    top: 44,
    border: `3px solid ${tokens.color.surface}`,
    backgroundColor: tokens.color.inkFaint,
    padding: 5,
    borderRadius: "50%",
    // `color="grey"` on <Badge> isn't a valid MUI palette key and throws
    // the same crash fixed elsewhere in this app — this override is the
    // only thing that should set the dot's color now.
  },
}));
